import React from "react"

import {WebEditorRuntimeContext} from "./layout"
import Editor from '@kehila/react-page-editor';

import {cellPlugins} from "./plugins/cellPlugins"
import {Alert, Form, notification, Spin, theme} from "antd"
import {captureException} from "@sentry/react"
import FormPlugin from "./formPlugins";
import {formEditorMiddleware} from './formPlugins/productInfoRebinding';
import {customPriceErrorMessage} from "./formPlugins/customPrice";

import ApiService from "../../services/ApiService"
import {t} from "@lingui/core/macro"
import {AccountAPI} from "../AccountSpace/api"
import {buildFormPrefill} from "../AccountSpace/prefillForm"
import {isAccountPrefillDisabled, setAccountPrefillDisabled} from "../AccountSpace/accountPrefillToggle"
import logger from "../../services/logger"
import { buildReturnUrl, navigateToPaiement } from "../../services/paiementLauncher"
import { isEmbedded, onHostUrlReady } from "../../services/embedHost"

function SubmissionError({message}) {
    const {token} = theme.useToken()
    return <Alert role="alert" type="error" showIcon message={message} style={{marginBlockEnd:token.marginLG}} />
}

//COMPATIBILITY SAFARI < 15.4
if(!Array.prototype.at)
{
    Array.prototype.at=function (index){
        if(index<0){
            index=this.length+index
        }
        if(index>=0 && index < this.length){
            return this[index]
        }
        return undefined
    }
}

export default class FormEditorRender extends React.Component {

    constructor(props) {
        super(props);
        if(props.getRef){
            props.getRef(this)
        }
    }

    state = {
        loading: true,
        plugins:[],
        // Espace compte (prefill) : profil du titulaire connecté. Reste null pour un
        // visiteur anonyme → getDefaultData() retombe sur le prefill localStorage
        // habituel (FORM_identity), comportement strictement inchangé.
        account: null,
        // « Ce n'est pas moi » → prefill compte coupé pour la session (sessionStorage,
        // propagé form↔paiement). Le compte reste chargé (réactivable).
        accountDisabled: isAccountPrefillDisabled(),
        submitting: false,
        submissionError: null,
    }

    formPlugin=new FormPlugin()

    async componentDidMount() {
        this.initForm()
        // Best effort, jamais bloquant : si une session compte existe, on récupère le
        // profil titulaire pour préremplir identité + champs custom (extras).
        if (this.props.runtime?.account !== false) this.loadAccount()
    }

    // Récupère le profil du TITULAIRE si une session compte (cookie HttpOnly) existe
    // et que le compte est VALIDE. Le changement de state.account fait évoluer la `key`
    // du <Form> → Antd le remonte avec les initialValues préremplies (cf. getDefaultData).
    // NB : on ne gate PAS sur getToken() — la session vit dans un cookie non lisible
    // en JS (partagé entre sous-domaines) ; c'est /me qui tranche (403 si anonyme).
    async loadAccount() {
        try {
            const res = await AccountAPI.me()
            if (!res?.success || res?.account?.state !== "VALIDE") return
            // Le sync formPlugin (verrou + contrôle) est posé dans render() avant chaque
            // rendu du <Form> → pas besoin de callback ici.
            this.setState({
                account: {
                    id: res.account.id,
                    selfContact: res.account.selfContact || null,
                    profile: res.account.profile || {},
                },
            })
        } catch (_) {
            // silencieux → mode anonyme conservé
        }
    }

    // « Compte actif » = un titulaire est connecté ET le visiteur n'a pas cliqué
    // « ce n'est pas moi ». Centralise la décision (lock identité + prefill + envoi de
    // identity.id).
    isAccountActive() {
        return !!this.state.account && !this.state.accountDisabled
    }

    // Pose sur l'instance formPlugin courante : le verrou d'identité civile + le contrôle
    // d'alerte (callbacks de bascule). Appelé à l'init du form et à chaque bascule.
    syncAccountToPlugin() {
        if (!this.formPlugin) return
        if (this.formPlugin.setLockIdentity) {
            this.formPlugin.setLockIdentity(this.isAccountActive())
        }
        if (this.formPlugin.setAccountControl) {
            const p = this.state.account?.profile || {}
            this.formPlugin.setAccountControl({
                hasAccount: !!this.state.account,
                disabled: !!this.state.accountDisabled,
                name: [p.prenom, p.nom].filter(Boolean).join(" ").trim() || null,
                onDisable: () => this.disableAccountPrefill(),
                onEnable: () => this.enableAccountPrefill(),
            })
        }
    }

    // « Ce n'est pas moi » : coupe l'usage du compte pour la session (propagé
    // form↔paiement). Le <Form> remonte (key) → initialValues SANS prefill compte ;
    // les valeurs déjà saisies sont conservées par Antd au remount tant que la clé
    // d'identité reste, mais ici on repart du prefill localStorage habituel (cohérent
    // avec un visiteur qui « n'utilise pas son compte »). Champs déverrouillés.
    disableAccountPrefill() {
        setAccountPrefillDisabled(true)
        this.setState({accountDisabled: true})   // sync formPlugin posé dans render()
    }

    // « Réutiliser mon compte » : réactive le prefill + le verrou.
    enableAccountPrefill() {
        setAccountPrefillDisabled(false)
        this.setState({accountDisabled: false})  // sync formPlugin posé dans render()
    }

    async componentDidUpdate(prevProps) {
        if(prevProps.form && this.props.form && JSON.stringify(prevProps.form) !== JSON.stringify(this.props.form)){
            this.initForm()
        }
    }


    async initForm()
    {
        var content=null
        if(this.props.form.content){
            content=JSON.parse(this.props.form.content)
        }
        this.formPlugin=new FormPlugin(this,this.props.form,content,null,true)
        this.formPlugin.setRuntime(this.props.runtime)
        var plugins = this.formPlugin.getPlugins(cellPlugins)
        this.formPlugin.setOrg(this.props.org)
        // Re-pose verrou + contrôle d'alerte sur la nouvelle instance de formPlugin
        // (utile au remount via la `key` du Form quand le compte est déjà chargé/basculé).
        this.syncAccountToPlugin()

        this.setState({plugins:plugins,loading:false})
    }

    onChange(v)
    {
        this.formPlugin.setTree(v)
        this.formPlugin.checkFormPlugins()
        this.props.onChange(v)
    }

    getValue()
    {
        if(this.props.form.content){
            return JSON.parse(this.props.form.content)
        }
        return null
    }

    getFields()
    {
        var fields=this.formPlugin.getFormFields()

        if(this.props.form.identity  && this.props.form.identity !== "NONE"){
            fields.unshift("Identité")
        }

        return fields
    }


    //LIVE METHODE

    getContactIdentity(dataIdentity)
    {
        const CONTACT_PROPERTIES=["homme","nom","prenom","email","tel","adresse","postal","ville","pays"]
        if(dataIdentity){
            const identity={}
            for(let k of Object.keys(dataIdentity)){
                if(CONTACT_PROPERTIES.includes(k)){
                    identity[k]=dataIdentity[k]
                }
            }

            return identity
        }

        return null
    }

    saveLocale(data)
    {
        if (this.props.runtime?.persistIdentity === false) return
        try{
            const identity=this.getContactIdentity(data["identity"])
            if(identity){
                localStorage.setItem("FORM_identity",JSON.stringify(identity))

                if(!localStorage.getItem("MEM_INFO")){
                    localStorage.setItem("MEM_INFO",JSON.stringify(identity))
                }

                if(!localStorage.getItem("MEM_FACTURATION")){
                    localStorage.setItem("MEM_FACTURATION",JSON.stringify({perso:true,...identity}))
                }
            }
        }
        catch (e){
            logger.error({ err: e }, "saveLocale")
        }
    }

    getDefaultData()
    {
        var defaultData={__paiementMethod:"En Ligne"}
        if (this.props.runtime?.persistIdentity === false) return defaultData
        try{
            if(localStorage.getItem("FORM_identity")){
                const identity=this.getContactIdentity(JSON.parse(localStorage.getItem("FORM_identity")))
                if(identity){
                    defaultData["identity"]=identity
                }
            }
        }catch(e){
            logger.error({ err: e }, "getDefaultData")
        }

        // Prefill Espace compte : si le titulaire est connecté (compte VALIDE) ET qu'il
        // n'a pas cliqué « ce n'est pas moi », on enrichit PAR-DESSUS le prefill
        // localStorage avec son identité, sa 1ʳᵉ adresse, son id (garde A7) et ses champs
        // custom (extras, arrosés racine + identity). Anonyme/désactivé → defaultData
        // inchangé (pas de identity.id → pas de rattachement au compte côté serveur).
        if(this.isAccountActive()){
            try{
                defaultData=buildFormPrefill(this.state.account, defaultData)
            }catch(e){
                logger.error({ err: e }, "getDefaultData prefill compte")
            }
        }

        return defaultData
    }

    async onSubmit(data)
    {
        if (this.submitting) return
        this.submitting = true
        this.setState({submitting: true, submissionError: null})
        if (this.props.runtime) this.props.runtime.submitting = true
        try {
        logger.debug({ data }, "form onSubmit")

        this.props.onLoading?.(true)
        this.saveLocale(data)

        if(this.props.extra){
            data.extra=this.props.extra
        }

        // Rattachement au contact du compte (garde A7 serveur). On pose data.identity.id
        // ICI et non via initialValues : il n'existe AUCUN <Form.Item name=["identity","id"]>
        // dans le module identité, donc Antd ne conserverait pas un id injecté dans
        // initialValues (il ne remonte au submit que les champs réellement enregistrés).
        // Le backend revalide cet id (submitForm → resolveBound) ; un id non rattaché est
        // ignoré. Anonyme / compte désactivé → on ne pose rien (comportement d'origine).
        let useProxy = false
        if(this.isAccountActive() && this.state.account?.selfContact && data.identity){
            data.identity.id = this.state.account.selfContact
            // Compte rattaché → router via le BFF same-origin pour que le cookie
            // de session parte (la garde A7 côté API valide alors data.identity.id).
            useProxy = true
        }

        // Langue de l'interface au moment de la soumission (context.langValue,
        // passée en prop par la page Form). Stockée sur la soumission côté API
        // -> tag de langue du contact (#FR/#EN/...). Transmise en query comme devise.
        const lang = this.props.lang || "fr"
        const submit = this.props.runtime?.submit || (async (values) => {
            const params = new URLSearchParams({devise: this.props.org.devise, lang})
            if (isEmbedded()) {
                // Capturer la page hôte AVANT de quitter l'iframe : le lien court
                // de paiement s'ouvrira ensuite en pleine fenêtre et l'aura perdue.
                await new Promise(resolve => onHostUrlReady(resolve))
                params.set("returnUrl", buildReturnUrl(this.props.org))
            }
            return ApiService.post("/forms/submit/"+this.props.form.id+"?"+params.toString(), values, {useProxy})
        })
        var res=await submit(data)
        logger.debug({ res }, "form submit response")
        if(res.success){
            if(res.url){
                if (this.props.runtime?.navigate) {
                    this.props.runtime.navigate(res.url, res.navigation)
                    return
                }
                let searchParams=new URLSearchParams(window.location.search)
                let finalUrl=res.url
                if(searchParams.size){
                    finalUrl+="?"+searchParams.toString()
                }
                logger.info({ finalUrl }, "redirect to")
                // La soumission vient d'attendre le serveur : le geste de
                // l'utilisateur a expiré. En mode intégré, navigateToPaiement
                // demande au site hôte de naviguer plutôt que de tenter une
                // sortie que le navigateur refuserait en silence.
                navigateToPaiement(finalUrl)
            }else{
                // 2e argument : lien de la page « mes billets » quand l'inscription
                // validée a des billets (affiché par l'écran de confirmation du thème).
                this.props.onSubmit?.(res.data, {ticketsUrl: res.ticketsUrl || null})
            }
        } else if (res.error?.startsWith('CUSTOM_PRICE_')) {
            const message = customPriceErrorMessage(res.error)
            this.formPlugin.formRef?.setFields([{name:'__customPrice', errors:[message]}])
            this.formPlugin.formRef?.scrollToField('__customPrice', {focus:true, block:'center'})
            this.formPlugin.notify('error', {message})
        } else if (this.props.form.annualEventProjectId) {
            const message = res.error === 'ANNUAL_EVENT_FULL' ? t`Aucune inscription disponible pour cette session.`
                : t`Une erreur est survenue. Réessayez dans un instant.`
            this.formPlugin.notify('error', {message})
            this.setState({submissionError: message})
        } else {
            this.setState({submissionError: t`Une erreur est survenue. Réessayez dans un instant.`})
        }
        } catch (error) {
            if (this.props.runtime?.onError) this.props.runtime.onError(error)
            else {
                // The form stays mounted with its values. Report the handled
                // failure once, without throwing out of the submit handler.
                captureException(error, {tags:{operation:'forms.submit'}, extra:{formId:this.props.form.id}})
                this.setState({submissionError: t`Une erreur est survenue. Réessayez dans un instant.`})
            }
        } finally {
            this.submitting = false
            if (this.props.runtime) {
                this.props.runtime.submitting = false
            }
            this.setState({submitting: false})
            this.props.onLoading?.(false)
        }
    }

    render()
    {

        const {form,lang,isMobile} = this.props

        // Pose le verrou d'identité + le contrôle d'alerte sur le formPlugin AVANT le
        // rendu du <Form> (donc avant que le plugin identity/ ne lise isIdentityLocked /
        // getAccountControl). Indispensable car au remount du <Form> via `key` (compte
        // chargé / bascule), le callback de setState s'exécute APRÈS le render → sans ça
        // le verrou + bandeau auraient un render de retard. syncAccountToPlugin ne fait
        // que muter le formPlugin (pas de setState) → safe dans render.
        this.syncAccountToPlugin()

        var content=null
        if(form.content){
            content=JSON.parse(form.content)
        }

        return (
            <div data-uc-role={this.props.runtime ? 'form-body' : undefined} style={this.props.runtime?.neutral ? {width:'100%', minWidth:0} : {width:1425,maxWidth:"98vw",padding:this.props.embed?"0":"5%",margin:"0 auto"}}>
                {this.state.submissionError && <SubmissionError message={this.state.submissionError} />}
                {this.state.loading &&(
                    <Spin spinning={true}/>
                )}

                {!this.state.loading &&(
                    <Form
                        noValidate
                        name={this.props.runtime?.instanceId}
                        disabled={this.state.submitting}
                        // initialValues n'est lu par Antd qu'au MONTAGE du Form. Le profil
                        // compte arrive en async après le 1er rendu, et la bascule « ce
                        // n'est pas moi » change le prefill → on fait varier la `key` sur
                        // (compte chargé + état activé/désactivé) pour forcer un remount
                        // avec les bonnes initialValues. Anonyme : key stable → aucun remount.
                        key={this.state.account ? ("acc-" + this.state.account.id + (this.state.accountDisabled ? "-off" : "-on")) : "anon"}
                        ref={(ref)=>this.formPlugin.setFormRef(ref)}
                        layout="vertical"
                        initialValues={this.getDefaultData()}
                        onFinishFailed={(e)=> {
                            this.formPlugin.notify('error', {
                                message:t`Champs obligatoires`,
                                description:t`Veuillez renseigner tous les champs obligatoires avant de soumettre le formulaire.`,
                                placement:"bottomRight"
                            })
                            this.formPlugin.formRef?.scrollToField(e.errorFields?.[0]?.name, {focus:true, block:'center'})
                        }}
                        onFinish={(data)=>{
                            if(this.formPlugin.isSubmitErrored(data)){
                                return false
                            }
                            this.onSubmit(data)
                        }}
                        onValuesChange={(_,allValues)=>{
                            this.formPlugin.setFormValues(allValues)
                            this.formPlugin.checkConditions()
                        }}
                    >
                        <WebEditorRuntimeContext.Provider value={this.props.runtime || null}><Editor
                            middleware={formEditorMiddleware}
                            cellPlugins={this.state.plugins}
                            value={this.formPlugin.hookValueEditor(content)}
                            onChange={(v)=>this.onChange(v)}
                            cellSpacing={this.props.runtime?.cellSpacing || {x:50,y:0}}
                            readOnly
                            lang={lang}
                        /></WebEditorRuntimeContext.Provider>
                    </Form>
                )}

            </div>
        )
    }

}
